import CardContainer from '@/components/cards/CardContainer';
import LineChart from '@/components/charts/LineChart';
import LineChartSkeleton from '@/components/charts/LineChartSkeleton';
import Container, { Column } from '@/components/container/Container';
import LoadingProgressBar from '@/components/utils/LoadingProgressBar';
import { COMPLETED_REQUESTS_VOLUME } from '@/constants/insights-page';
import { ORDERS_VOLUME } from '@/constants/labels';
import { addDays, addMonths, addWeeks, format, isBefore } from 'date-fns';
import _ from 'lodash';
import resolveConfig from 'tailwindcss/resolveConfig';
import themeColor from '../../../../styles/colors.json';
import tailwindConfig from '../../../../tailwind.config';
import ChartLabel from './ChartLabel';
import EmptyScreen from './EmptyScreen';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { GetInsightsRequest, Insights4SpendingDto, Insights4VolumeDto } from '../views/InsightsScreen';
import { axiosInstance } from '@/services/axios.service';
import { GenericResponse } from '@/common/common_utils';
import { ENDPOINTS } from '@/constants/endpoints';
import moment from 'moment';

type DateRange = {
	start: Date | null;
	end: Date | null;
};

type Props = {
	// hasError: boolean | null;
	// hasData: boolean | null;
	// isLoaded: boolean;
	date: string | number;
	title: string;
	//chartData: ChartData;
	dateRange: DateRange | null;
	viewMode: 'week' | 'day';
	setViewMode: React.Dispatch<React.SetStateAction<'week' | 'day'>>;
	program: string;
};

const CompletedRequestVolumeSection = ({
	//hasError,
	//hasData,
	//isLoaded,
	date,
	title,
	//chartData,
	viewMode,
	setViewMode,
	dateRange,
	program,
	...props
}: Props) => {

	const groupBy = (date == 'today' ? 'hour' : (viewMode == 'day' ? 'day' : 'week'))
	const insightsQ = useQuery({
		queryKey: ['Insights.Volume', date, dateRange, viewMode],
		queryFn: async () => {
			// const result = await axiosInstance.post<GenericResponse<InsightsDto>>(ENDPOINTS['/api/payments/GetInsights'], { Start: moment().startOf('month').toDate(), End: moment().toDate() } as GetInsightsRequest);
			const result = await axiosInstance.post<GenericResponse<Insights4VolumeDto>>(ENDPOINTS['/api/payments/GetInsights4Volume'], { Start: dateRange?.start, End: dateRange?.end, GroupBy: groupBy } as GetInsightsRequest);

			return result.data.Data;
		},
		enabled: !!(dateRange?.start && dateRange?.end),
	});

	const renderBody = () => {
		const hasMode = date !== 'today' && date !== 7;

		const stateTemplate = () => {
			if (!insightsQ.isFetched || !insightsQ.isError)
				return (
					<Column gap="gap-4">
						<Container className="-mt-6">
							<LoadingProgressBar />
						</Container>
						<LineChartSkeleton />
					</Column>
				);

			if (insightsQ.data?.Volume.length == 0) {
				return <EmptyScreen isError={false} height="h-[470px]" />;
			}

			if (insightsQ.isError) {
				return (
					<EmptyScreen
						isError={insightsQ.isError}
						// hasError={hasError}
						height="h-[470px]"
					/>
				);
			}
		};

		if (!insightsQ.isFetched) {
			return stateTemplate();
		}

		let labels: string[] = [];

		if (date == 'today') {
			labels = insightsQ.data?.Volume.map(i => moment(i.Date).format('hh:mm')) ?? [];
		} else if (date == 7 || viewMode == 'day') {
			labels = insightsQ.data?.Volume.map(i => moment(i.Date).format('DD/YY')) ?? [];
		} else {//'week'
			labels = insightsQ.data?.Volume.map(i => {
				const startDate = moment(i.Date);
				const endDate = moment(i.Date).add(6, 'days');
				return `${startDate.format('MMM DD')} - ${endDate.format('DD')}`;
			}) ?? [];
		}

		const lineData = {
			labels,
			datasets: [
				{
					label: 'Travel',
					data: insightsQ.data?.Volume.map(i => i.Value),
					backgroundColor: [themeColor.tertiary],
					borderColor: [themeColor.tertiary],
					borderWidth: 3,
					pointStyle: false,
				},
			],
		};

		const maxY = (insightsQ.data?.Volume.reduce((a, b) => a < b.Value ? b.Value : a, 0) ?? 100);

		const fullConfig = resolveConfig(tailwindConfig);

		const axesLabelStyle = {
			display: true,
			font: {
				size: 16,
				family: fullConfig.theme.fontFamily.poppins,
				weight: 600,
			},
			color: themeColor.primaryFixedDim,
			padding: 10,
		};

		const ticksStyleX = {
			display: true,
			font: {
				size: 8,
				family: fullConfig.theme.fontFamily.poppins,
				weight: 400,
				lineHeight: 2,
			},
			color: themeColor.secondary,
		};

		const ticksStyleY = {
			display: true,
			font: {
				size: 10,
				family: fullConfig.theme.fontFamily.poppins,
				weight: 400,
			},
			color: themeColor.chartTickY,
		};

		const lineOptions = {
			maintainAspectRatio: false,
			aspectRatio: 0.8,
			plugins: {
				legend: {
					display: false,
				},
			},
			scales: {
				x: {
					ticks: {
						...ticksStyleX,
					},
					grid: {
						display: false,
					},
					title: {
						text: title,
						...axesLabelStyle,
					},
				},
				y: {
					min: 0,
					max: maxY,
					ticks: {
						...ticksStyleY,
						stepSize: 1
					},
					grid: {
						display: false,
					},
					title: {
						text: ORDERS_VOLUME,
						...axesLabelStyle,
					},
				},
			},
		};
		return <div className="flex flex-col space-y-7">
			<div className="flex flex-col space-y-2">
				<ChartLabel
					label={`${COMPLETED_REQUESTS_VOLUME} ${_.lowerFirst(title)}`}
					value={`${insightsQ.data?.Volume.reduce((a, b) => a + b.Value, 0).toLocaleString()}`}
					hasMode={hasMode}
					viewMode={viewMode}
					setViewMode={setViewMode}
				/>
				<div className="flex gap-3 items-center">
					<div className="bg-info h-[18px] w-[18px]" />
					<p className="text-label-semibold text-neutral50 font-poppins">
						{_.upperFirst(program)}
					</p>
				</div>
			</div>
			<LineChart data={lineData} options={lineOptions} />
		</div>
	};


	return (
		<CardContainer>
			<Container className="p-6 bg-surfaceBright rounded-[24px]">
				{renderBody()}
			</Container>
		</CardContainer>
	);
};

export default CompletedRequestVolumeSection;
